import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Scale, Compass, ShieldCheck, BookOpen, ArrowRight, PhoneCall } from "lucide-react";
import { GoldBadge, SectionHead, Bullets, Reveal, MaskedLines, Marquee } from "@/components/shared";
import DonateSection from "@/components/DonateSection";
import HomeAnthem from "@/components/HomeAnthem";
import PartnerStrip from "@/components/PartnerStrip";
import FoundersNote from "@/components/FoundersNote";

const MARQUEE_ITEMS = [
  "Child Abuse Prevention",
  "Advocacy & Court Support",
  "Community Safety Patrols",
  "Breaking The Silence",
  "Seeds To Be Grown",
  "Born From Broken",
];

const CHAPTERS = [
  {
    numeral: "01",
    title: "The Silence",
    text: "For 45 years, institutions counted on our quiet. We counted the years instead — every name, every date, every locked door, recorded and carried to the nation's highest inquiry.",
    testId: "chapter-silence",
  },
  {
    numeral: "02",
    title: "The Stone",
    text: "They tried to bury us beneath the stone. Stone is heavy. So is the truth — and on 31 May 2017, the weight shifted permanently onto the record.",
    testId: "chapter-stone",
  },
  {
    numeral: "03",
    title: "The Seed",
    text: "They did not know we were seeds to be grown. What grew is a charity on the street, in the courts, and beside every survivor who refuses to stay buried.",
    testId: "chapter-seed",
  },
];

const HERO_IMG =
  "https://images.unsplash.com/photo-1618661733134-6a750e4aa08a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxhdXN0cmFsaWFuJTIwbGFuZHNjYXBlJTIwZGFyayUyMHNvbGVtbiUyMHNvbGVtbiUyMGhvcGUlMjBnb2xkZW4lMjBsaWdodHxlbnwwfHx8fDE3ODk0MTIxNTV8MA&ixlib=rb-4.1.0&q=85";
const BOOK_IMG = "/assets/bfb-cover.webp";

const STATS = [
  { value: "45", label: "Years of recorded institutional history", testId: "stat-years" },
  { value: "2017", label: "Royal Commission private session victory", testId: "stat-royal-commission" },
  { value: "10+", label: "Years of registered non-profit operation", testId: "stat-decade" },
  { value: "4", label: "Active frontline branches & initiatives", testId: "stat-branches" },
];

const SERVICE_TEASERS = [
  {
    icon: Compass,
    title: "Advisory & Mentoring",
    text: "Direct survival coaching and guidance frameworks for survivors rebuilding their lives.",
    testId: "teaser-advisory-card",
  },
  {
    icon: Scale,
    title: "Advocacy & Court Support",
    text: "Standing face-to-face with institutions and walking beside survivors through legal systems.",
    testId: "teaser-advocacy-card",
  },
  {
    icon: ShieldCheck,
    title: "Community Safety Patrols",
    text: "Active mobile observation initiatives lowering escalating child abuse statistics.",
    testId: "teaser-patrols-card",
  },
];

function HeroArt() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 200]);
  return (
    <motion.div style={{ y }} className="absolute inset-0" aria-hidden="true">
      <motion.img
        src={HERO_IMG}
        alt=""
        className="h-full w-full object-cover"
        initial={{ scale: 1.15, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
      />
    </motion.div>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative grain-overlay overflow-hidden min-h-[92vh] flex items-center">
        <HeroArt />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14] via-[#0A0D14]/75 to-[#0A0D14]/35" />
        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 py-24 sm:py-32 grid gap-12 lg:grid-cols-[1.25fr_1fr] lg:items-center">
          <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <GoldBadge testId="hero-badge">Registered Australian Charity • Founder: Ronald Waters</GoldBadge>
          </motion.div>
          <h1 className="font-display mt-6 text-[12.5vw] sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.04] text-slate-50 max-w-5xl">
            <MaskedLines lines={["People Against", "Intentional Neglect"]} />
            <MaskedLines lines={["(P.A.I.N.)"]} baseDelay={0.45} lineClassName="gold-text-gradient" />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-300"
          >
            Dedicated to child abuse prevention and breaking decades of institutional silence —
            with survivors, not behind them.
          </motion.p>
          <motion.blockquote
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.95 }}
            className="mt-8 max-w-2xl border-l-2 border-amber-500/70 pl-5"
          >
            <p className="font-display text-lg sm:text-xl italic text-amber-200/95 leading-relaxed">
              "They tried to bury us beneath the stone. They did not know we were seeds to be grown."
            </p>
          </motion.blockquote>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.15 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/services"
              data-testid="hero-services-button"
              className="btn-royal inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold text-white"
            >
              Access the Global Service Index
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-survivor-portal-button"
              className="btn-gold-outline inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold text-amber-300"
            >
              <PhoneCall className="h-4 w-4" />
              Survivor Safe Portal
            </Link>
          </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.94, rotate: 2 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="spotlight-frame relative rounded-xl overflow-hidden border border-amber-500/25 shadow-2xl max-w-md mx-auto lg:max-w-none w-full"
          >
            <img
              src="/assets/pain-ribbon-logo.webp"
              alt="The P.A.I.N. blue ribbon — People Against Intentional Neglect"
              className="w-full h-auto object-cover"
              data-testid="hero-ribbon-artwork"
            />
          </motion.div>
        </div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.7, duration: 1 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 font-code text-[10px] uppercase tracking-[0.3em] text-slate-500"
          aria-hidden="true"
        >
          Scroll
        </motion.p>
      </section>

      <Marquee items={MARQUEE_ITEMS} />

      <HomeAnthem />

      {/* MISSION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-start">
          <Reveal>
            <SectionHead
              eyebrow="01 — Our Mission"
              title="Breaking the silence. Building the shield."
              description="P.A.I.N. is a registered Australian charity founded by Ronald Waters — a survivor who carried 45 years of institutional truth into the nation's highest inquiry, and came out the other side with a mandate to protect the next generation."
            />
          </Reveal>
          <Reveal delay={0.15}>
            <div className="card-pain p-6 sm:p-8" data-testid="mission-points-card">
              <h3 className="font-display text-lg sm:text-xl font-medium text-slate-50 mb-5">
                What we stand for
              </h3>
              <Bullets
                items={[
                  "Child abuse prevention through visible, frontline community action",
                  "Breaking decades of institutional silence — on the record, permanently",
                  "Survivor-led advocacy founded and directed by Ronald Waters",
                  "Zero tolerance for systems that protect reputations over children",
                ]}
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="border-t border-white/10 bg-[#0D111B] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
          <Reveal>
            <p className="font-code text-xs sm:text-sm uppercase tracking-[0.2em] text-amber-400/90 mb-3">
              The Manifesto
            </p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-50 max-w-3xl">
              Three chapters. One unbroken record.
            </h2>
            <div className="h-[2px] w-16 bg-gradient-to-r from-amber-400 via-amber-600 to-transparent my-4" />
          </Reveal>
          <div className="mt-14 space-y-20 sm:space-y-24">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.testId} delay={0.05}>
                <div
                  data-testid={c.testId}
                  className={`relative flex flex-col gap-4 sm:max-w-2xl ${
                    i % 2 === 1 ? "sm:ml-auto sm:items-end sm:text-right" : ""
                  }`}
                >
                  <span
                    className={`ghost-numeral absolute -top-12 sm:-top-16 ${
                      i % 2 === 1 ? "right-0" : "left-0"
                    }`}
                    aria-hidden="true"
                  >
                    {c.numeral}
                  </span>
                  <h3 className="font-display relative text-xl sm:text-2xl font-medium text-amber-200/95 tracking-wide">
                    {c.title}
                  </h3>
                  <p className="relative text-base sm:text-lg text-slate-300 leading-relaxed">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <FoundersNote />

      {/* STATS */}
      <section className="border-y border-white/10 bg-[#111622]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <Reveal key={s.testId} delay={i * 0.08}>
              <div data-testid={s.testId} className="text-center lg:text-left">
                <p className="font-display text-3xl sm:text-4xl font-bold gold-text-gradient">{s.value}</p>
                <p className="mt-2 text-xs sm:text-sm text-slate-400 leading-snug">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICE TEASER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
        <Reveal>
          <SectionHead
            eyebrow="02 — The Global Service Index"
            title="Frontline intervention, structured and accountable"
            description="Three authoritative branches of direct intervention and survivor support — indexed, transparent, and active in the community."
          />
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {SERVICE_TEASERS.map((s, i) => (
            <Reveal key={s.testId} delay={i * 0.1}>
              <div data-testid={s.testId} className="card-pain p-6 sm:p-8 h-full">
                <s.icon className="h-8 w-8 text-amber-400" />
                <h3 className="font-display mt-5 text-xl font-medium text-slate-50">{s.title}</h3>
                <p className="mt-3 text-sm sm:text-base text-slate-300 leading-relaxed">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-10">
            <Link
              to="/services"
              data-testid="services-index-cta-link"
              className="btn-gold-outline inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold text-amber-300"
            >
              View the full Global Service Index
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* BORN FROM BROKEN TEASER */}
      <section className="border-y border-white/10 bg-[#111622] grain-overlay relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 grid gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <div className="spotlight-frame relative rounded-xl overflow-hidden border border-amber-500/25 shadow-2xl -rotate-2 hover:rotate-0 transition-transform duration-500">
              <img
                src={BOOK_IMG}
                alt="Born From Broken — the autobiography of Ronald Waters"
                className="w-full h-72 sm:h-96 object-cover object-right"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14]/90 to-transparent" />
              <div className="absolute bottom-4 left-4">
                <GoldBadge testId="book-teaser-badge">Official Autobiography</GoldBadge>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <SectionHead
              eyebrow="03 — Born From Broken"
              title="45 years. One voice. A nation's record."
              description="The official autobiographical campaign, media portal, and anthem hub mapping Ronald Waters' journey from state ward to historical Royal Commission victory."
            />
            <Bullets
              items={[
                "Pre-order the Born From Broken autobiography",
                "Stream the companion anthem tracks",
                "Follow the media portal documenting a 45-year fight for truth",
              ]}
            />
            <Link
              to="/contact"
              data-testid="book-preorder-cta-button"
              className="btn-royal mt-8 inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold text-white"
            >
              <BookOpen className="h-4 w-4" />
              Pre-Order & Stream
            </Link>
          </Reveal>
        </div>
      </section>

      <PartnerStrip />

      <DonateSection />

      {/* EMERGENCY BANNER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-24">
        <Reveal>
          <div
            data-testid="emergency-banner"
            className="card-pain relative overflow-hidden p-8 sm:p-12 text-center"
          >
            <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-amber-500 to-transparent" />
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-slate-50">
              If you are in immediate danger, call <span className="gold-text-gradient">000</span> now.
            </h2>
            <p className="mt-4 max-w-2xl mx-auto text-sm sm:text-base text-slate-300 leading-relaxed">
              Our survivor safe portal is open for confidential court support requests, mentoring
              enquiries, and community safety reports. You will be believed here.
            </p>
            <Link
              to="/contact"
              data-testid="emergency-portal-button"
              className="btn-royal mt-8 inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold text-white"
            >
              <PhoneCall className="h-4 w-4" />
              Reach Out Securely
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
